
import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Wallet } from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';
import { RevenueStats } from './types';

interface RevenueStatsCardsProps {
  stats: RevenueStats;
  programCount: number;
  teacherPaymentsCount: number;
}

export const RevenueStatsCards = ({ stats, programCount, teacherPaymentsCount }: RevenueStatsCardsProps) => {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  
  // Enhanced 3D effect with mouse tracking
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>, index: number) => {
    if (hoveredCard !== index) return;
    
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    
    // Calculate mouse position relative to card center
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    
    // Normalize values (-1 to 1)
    const normalizedX = x / (rect.width / 2);
    const normalizedY = y / (rect.height / 2);
    
    // Apply the transformation
    card.style.transform = `perspective(1000px) rotateY(${normalizedX * 5}deg) rotateX(${-normalizedY * 5}deg) translateZ(15px)`;
    card.style.boxShadow = '0 25px 50px rgba(0, 0, 0, 0.25), 0 0 40px rgba(0, 128, 0, 0.15)';
  };
  
  const handleMouseEnter = (index: number) => {
    setHoveredCard(index);
  };
  
  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    setHoveredCard(null);
    e.currentTarget.style.transform = 'perspective(1000px) rotateY(0) rotateX(0) translateZ(0)';
    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
  };

  const cardStyle = "transition-all duration-300 card-3d-container";

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <Card 
        className={`p-4 bg-slate-900 text-white ${cardStyle}`}
        onMouseMove={(e) => handleMouseMove(e, 0)}
        onMouseEnter={() => handleMouseEnter(0)}
        onMouseLeave={handleMouseLeave}
      >
        <p className="text-sm text-gray-300">Total Revenue</p>
        <p className="text-2xl font-bold text-green-400">AFN {formatCurrency(stats.totalRevenue)}</p>
        <p className="text-xs text-gray-400 mt-1">From {programCount} program enrollments</p>
      </Card>
      
      <Card 
        className={`p-4 bg-slate-900 text-white ${cardStyle}`}
        onMouseMove={(e) => handleMouseMove(e, 1)}
        onMouseEnter={() => handleMouseEnter(1)}
        onMouseLeave={handleMouseLeave}
      >
        <p className="text-sm text-gray-300">Paid</p>
        <p className="text-2xl font-bold text-green-400">AFN {formatCurrency(stats.paidAmount)}</p>
        <p className="text-xs text-gray-400 mt-1">{stats.totalRevenue > 0 ? Math.round((stats.paidAmount / stats.totalRevenue) * 100) : 0}% of total revenue</p>
      </Card>
      
      {/* Only show Pending card if there are actual pending payments */}
      {stats.pendingAmount > 0 && (
        <Card 
          className={`p-4 bg-slate-900 text-white ${cardStyle}`}
          onMouseMove={(e) => handleMouseMove(e, 2)}
          onMouseEnter={() => handleMouseEnter(2)}
          onMouseLeave={handleMouseLeave}
        >
          <p className="text-sm text-gray-300">Pending</p>
          <p className="text-2xl font-bold text-red-400">AFN {formatCurrency(stats.pendingAmount)}</p>
          <p className="text-xs text-gray-400 mt-1">{Math.round((stats.pendingAmount / stats.totalRevenue) * 100)}% of total revenue</p>
        </Card>
      )}
      
      {/* Only show Partial Payments card if there are actual partial payments */}
      {stats.partialAmount > 0 && (
        <Card 
          className={`p-4 bg-slate-900 text-white ${cardStyle}`}
          onMouseMove={(e) => handleMouseMove(e, 3)}
          onMouseEnter={() => handleMouseEnter(3)}
          onMouseLeave={handleMouseLeave}
        >
          <p className="text-sm text-gray-300">Partial Payments</p>
          <p className="text-2xl font-bold text-yellow-400">AFN {formatCurrency(stats.partialAmount)}</p>
          <p className="text-xs text-gray-400 mt-1">{Math.round((stats.partialAmount / stats.totalRevenue) * 100)}% of total revenue</p>
        </Card>
      )}
      
      {stats.teacherSalaries > 0 && (
        <Card 
          className={`p-4 bg-slate-800 text-white lg:col-span-2 ${cardStyle}`}
          onMouseMove={(e) => handleMouseMove(e, 4)}
          onMouseEnter={() => handleMouseEnter(4)}
          onMouseLeave={handleMouseLeave}
        >
          <div className="flex items-center gap-2">
            <span className="font-semibold text-red-400">AFN</span>
            <p className="text-sm text-gray-300">Teacher Salary Expenses</p>
          </div>
          <p className="text-2xl font-bold text-red-400">AFN {formatCurrency(stats.teacherSalaries)}</p>
          <p className="text-xs text-gray-400 mt-1">From {teacherPaymentsCount} teacher payments</p>
        </Card>
      )}
      
      {stats.teacherSalaries > 0 && (
        <Card 
          className={`p-4 bg-emerald-900 text-white lg:col-span-2 ${cardStyle}`}
          onMouseMove={(e) => handleMouseMove(e, 5)}
          onMouseEnter={() => handleMouseEnter(5)}
          onMouseLeave={handleMouseLeave}
        >
          <div className="flex items-center gap-2">
            <Wallet className={`h-5 w-5 text-emerald-300 ${hoveredCard === 5 ? 'animate-bounce' : ''}`} />
            <p className="text-sm text-gray-300">Net Revenue After Expenses</p>
          </div>
          <p className="text-2xl font-bold text-emerald-300">
            AFN {formatCurrency(stats.totalRevenue - stats.teacherSalaries)}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {Math.round(((stats.totalRevenue - stats.teacherSalaries) / stats.totalRevenue) * 100)}% profit margin
          </p>
        </Card>
      )}
    </div>
  );
};
